'use client';

import {
  createContext,
  ReactNode,
  useContext,
  useEffect,
  useState,
} from 'react';

interface FavoritesContextType {
  addFavoriteOffer: (offerId: string) => void;
  favoriteCount: number;
  favoriteOffers: string[];
  isFavorite: (offerId: string) => boolean;
  removeFavoriteOffer: (offerId: string) => void;
}

const FavoritesContext = createContext<FavoritesContextType | undefined>(
  undefined,
);

const getLocalStorage = (key: string) => {
  if (typeof window === 'undefined') return [];

  const item = localStorage.getItem(key);

  return item ? JSON.parse(item) : [];
};

const setLocalStorage = (key: string, value: any) => {
  if (typeof window !== 'undefined') {
    localStorage.setItem(key, JSON.stringify(value));
  }
};

export const FavoritesProvider = ({ children }: { children: ReactNode }) => {
  const [favoriteOffers, setFavoriteOffers] = useState<string[]>(() =>
    getLocalStorage('favoriteOffers'),
  );

  useEffect(() => {
    setLocalStorage('favoriteOffers', favoriteOffers);
  }, [favoriteOffers]);

  const addFavoriteOffer = (offerId: string) => {
    setFavoriteOffers(previousOffers =>
      previousOffers.includes(offerId)
        ? previousOffers
        : [...previousOffers, offerId],
    );
  };

  const removeFavoriteOffer = (offerId: string) => {
    setFavoriteOffers(previousOffers =>
      previousOffers.filter(offer => offer !== offerId),
    );
  };

  const isFavorite = (offerId: string) => {
    return favoriteOffers.includes(offerId);
  };

  return (
    <FavoritesContext.Provider
      value={{
        addFavoriteOffer,
        favoriteCount: favoriteOffers.length,
        favoriteOffers,
        isFavorite,
        removeFavoriteOffer,
      }}
    >
      {children}
    </FavoritesContext.Provider>
  );
};

export const useFavoritesContext = () => {
  const context = useContext(FavoritesContext);

  if (!context) {
    throw new Error(
      'useFavoritesContext must be used within a FavoritesProvider',
    );
  }

  return context;
};
